import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import BrandLogoBadge from "./ui/BrandLogoBadge";

const AUTH_STORAGE_KEY = "fsrov_auth";
const FIXED_USERNAME = "tb1rov";
const FIXED_PASSWORD = "tb1xfs";

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Rajdhani:wght@400;500;600;700&display=swap');
    @keyframes scan { 0% { top: -10%; opacity: 0; } 50% { opacity: 1; } 100% { top: 110%; opacity: 0; } }
  `}</style>
);

const Glow = ({ c, s = 400, x, y }) => (
  <div
    style={{
      position: "absolute",
      width: s,
      height: s,
      left: x,
      top: y,
      borderRadius: "50%",
      filter: "blur(100px)",
      background: `radial-gradient(circle, ${c}80 0%, ${c}40 30%, transparent 70%)`,
      opacity: 0.6,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

const Grid = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage:
        "linear-gradient(rgba(255,16,38,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,16,38,0.05) 1px, transparent 1px)",
      backgroundSize: "60px 60px",
      opacity: 0.3,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

const HexPattern = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0 L45 15 L45 45 L30 60 L15 45 L15 15 Z' fill='none' stroke='%23ff1026' stroke-width='1'/%3E%3C/svg%3E")`,
      backgroundSize: "60px 60px",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

const ScanLine = () => (
  <div
    style={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      borderRadius: "16px",
      zIndex: 10,
    }}
  >
    <div
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        height: "2px",
        background: "linear-gradient(90deg, transparent, #ff1026, transparent)",
        animation: "scan 3s linear infinite",
        boxShadow: "0 0 10px #ff1026",
      }}
    />
  </div>
);

const CornerAccents = ({ color = "#ff1026" }) => (
  <>
    <div style={{ position: "absolute", top: -2, left: -2, width: 20, height: 20, borderTop: `2px solid ${color}`, borderLeft: `2px solid ${color}`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", top: -2, right: -2, width: 20, height: 20, borderTop: `2px solid ${color}`, borderRight: `2px solid ${color}`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", bottom: -2, left: -2, width: 20, height: 20, borderBottom: `2px solid ${color}`, borderLeft: `2px solid ${color}`, pointerEvents: "none" }} />
    <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, borderBottom: `2px solid ${color}`, borderRight: `2px solid ${color}`, pointerEvents: "none" }} />
  </>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  function handleSubmit(e) {
    e.preventDefault();
    const user = username.trim();
    const pass = password.trim();

    if (user === FIXED_USERNAME && pass === FIXED_PASSWORD) {
      localStorage.setItem(AUTH_STORAGE_KEY, "true");
      setErrorMessage("");
      navigate("/upload");
      return;
    }

    setErrorMessage("ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
  }

  function handleForgotPassword() {
    window.alert("ลืมรหัสผ่าน: หน้านี้เป็น mockup กรุณาติดต่อผู้ดูแลระบบ");
  }

  return (
    <>
      <GlobalStyles />
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(180deg, #ffffff 0%, #fff2f4 100%)",
          position: "relative",
          overflow: "hidden",
          padding: "16px",
          fontFamily: "'Rajdhani', sans-serif",
        }}
      >
        <div style={{ position: "absolute", inset: 0 }}>
          <Glow c="#ff1026" s={800} x="-20%" y="-10%" />
          <Glow c="#ff4d5f" s={600} x="80%" y="70%" />
          <Grid />
          <HexPattern />
        </div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            position: "relative",
            zIndex: 10,
            width: "100%",
            maxWidth: "420px",
            background: "rgba(255, 255, 255, 0.94)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,16,38,0.3)",
            borderRadius: "16px",
            padding: "30px 24px",
            boxSizing: "border-box",
            boxShadow: "0 12px 40px rgba(255,16,38,0.16)",
            color: "#111827",
          }}
        >
          <ScanLine />
          <CornerAccents />

          <div style={{ marginBottom: "16px", textAlign: "center" }}>
            <BrandLogoBadge size={120} radius={18} />
            <div
              style={{
                marginTop: "8px",
                fontFamily: "'Orbitron', sans-serif",
                letterSpacing: "0.08em",
                color: "#b91c1c",
                fontWeight: 700,
              }}
            >
              TO BE NUMBER ONE ESPORTS
            </div>
          </div>

          <label style={{ display: "block", marginBottom: "8px", color: "#b91c1c", letterSpacing: "0.06em" }}>
            USERNAME
          </label>
          <input
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
            placeholder="กรอกชื่อผู้ใช้"
            style={{
              width: "100%",
              padding: "12px",
              marginBottom: "14px",
              borderRadius: "10px",
              border: "1px solid rgba(255,16,38,0.45)",
              background: "#ffffff",
              color: "#000000",
              WebkitTextFillColor: "#000000",
              caretColor: "#000000",
              boxSizing: "border-box",
              outline: "none",
            }}
          />

          <label style={{ display: "block", marginBottom: "8px", color: "#b91c1c", letterSpacing: "0.06em" }}>
            PASSWORD
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (errorMessage) setErrorMessage("");
            }}
            placeholder="กรอกรหัสผ่าน"
            style={{
              width: "100%",
              padding: "12px",
              marginBottom: "18px",
              borderRadius: "10px",
              border: "1px solid rgba(255,16,38,0.45)",
              background: "#ffffff",
              color: "#000000",
              WebkitTextFillColor: "#000000",
              caretColor: "#000000",
              boxSizing: "border-box",
              outline: "none",
            }}
          />
          {errorMessage && (
            <div
              style={{
                marginBottom: "14px",
                color: "#ff8f8f",
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            style={{
              width: "100%",
              padding: "13px",
              borderRadius: "12px",
              border: "none",
              background: "linear-gradient(135deg, #ff1026, #a30013)",
              color: "#111827",
              fontWeight: 700,
              fontFamily: "'Orbitron', sans-serif",
              letterSpacing: "0.06em",
              cursor: "pointer",
              boxShadow: "0 0 20px rgba(255,16,38,0.4)",
            }}
          >
            SIGN IN
          </button>

          <button
            type="button"
            onClick={handleForgotPassword}
            style={{
              marginTop: "10px",
              width: "100%",
              padding: "11px",
              borderRadius: "12px",
              border: "1px solid rgba(255,16,38,0.5)",
              background: "rgba(255,16,38,0.08)",
              color: "#b91c1c",
              fontWeight: 700,
              fontFamily: "'Rajdhani', sans-serif",
              letterSpacing: "0.05em",
              cursor: "pointer",
            }}
          >
            ลืมรหัสผ่าน
          </button>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              color: "#7f1d1d",
              fontSize: "1rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textShadow: "0 0 12px rgba(255,16,38,0.45)",
            }}
          >
            <span
              style={{
                width: "36px",
                height: "1px",
                background: "linear-gradient(90deg, transparent, rgba(255,16,38,0.9))",
              }}
            />
            <img
              src={`${process.env.PUBLIC_URL}/ctrlg.png`}
              alt="ctrlg logo"
              style={{ width: "20px", height: "20px", objectFit: "contain" }}
            />
            <span>powered by ctrlg</span>
            <span
              style={{
                width: "36px",
                height: "1px",
                background: "linear-gradient(90deg, rgba(255,16,38,0.9), transparent)",
              }}
            />
          </div>
        </motion.form>
      </div>
    </>
  );
}
